import React from 'react';
import {
  MoreVertical,
  Users,
  FileText,
  Pin,
  Trash2,
  Briefcase,
  Plus,
  Eye,
  EyeOff,
  Megaphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '@/Components/ui/typography';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Components/ui/table';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Components/ui/tooltip';
import { Card, CardContent } from '@/Components/ui/card';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';
import { Can } from '@/HireWithUs/Context/CompanyAccessContext';

function roleStatus(job) {
  return String(job?.requirement_status || job?.requirementStatus || '').trim().toLowerCase();
}

function formatRoleDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * JobsTable - A reusable component for displaying jobs in a table format
 * 
 * @param {Array} jobs - Array of job objects to display
 * @param {boolean} loading - Loading state
 * @param {string} error - Error message if any
 * @param {string} title - Title to display above the table (default: "All Jobs")
 * @param {Function} handleViewApplications - Callback when "View Applications" is clicked
 * @param {Function} onViewJobDescription - Callback when "View Job Description" is clicked
 * @param {Function} onPinJob - Callback when "Pin Job" is clicked
 * @param {Function} onDeleteJob - Callback when "Delete Job" is clicked
 * @param {Function} onBroadcastJob - Callback when "Broadcast Job" is clicked (optional)
 * @param {React.Component} emptyStateComponent - Optional custom empty state component
 * @param {React.Component} loadingComponent - Optional custom loading component
 */
function JobsTable({
  jobs = [],
  loading = false,
  error = null,
  title = "All Jobs",
  handleViewApplications,
  onViewJobDescription,
  onPinJob,
  onDeleteJob,
  onBroadcastJob,
  // Public-board visibility — optional. When provided, each row gets an
  // Eye/EyeOff toggle button next to the actions.
  //   visibilityMap: { [job_id]: bool }    — current per-job state
  //   onToggleVisibility(job_id, next)     — flip handler
  //   companyIsPublic                      — when false, show a "won't appear
  //                                          publicly until master switch is on"
  //                                          tooltip on enabled-but-hidden jobs
  visibilityMap,
  onToggleVisibility,
  companyIsPublic = false,
  emptyStateComponent,
  loadingComponent,
}) {
  const navigate = useNavigate();

  const handleDropdownAction = (action, jobId) => {
    switch (action) {
      case 'view':
        onViewJobDescription && onViewJobDescription(jobId);
        break;
      case 'pin':
        onPinJob && onPinJob(jobId);
        break;
      case 'broadcast':
        onBroadcastJob && onBroadcastJob(jobId);
        break;
      case 'delete':
        onDeleteJob && onDeleteJob(jobId);
        break;
      default:
        break;
    }
  };

  // Show loading state
  if (loading && jobs.length === 0) {
    if (loadingComponent) {
      return loadingComponent;
    }
    return null; // Let parent handle loading state
  }

  // Show error state
  if (error && jobs.length === 0) {
    return (
      <section className="w-full py-4">
        <Heading level="h4" className="mb-4">
          {title}
        </Heading>
        <div className="text-destructive">
          <p>Error loading jobs: {error}</p>
        </div>
      </section>
    );
  }

  // Show empty state
  if (jobs.length === 0) {
    if (emptyStateComponent) {
      return emptyStateComponent;
    }
    
    return (
      <section className="w-full py-4">
        <Heading level="h4" className="mb-4">
          {title}
        </Heading>
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 px-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
              <Briefcase className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No jobs found
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">
              You haven't posted any jobs yet. Create a new job posting to get started with hiring.
            </p>
            <Can permission="jobs.create">
            <Button
              onClick={() => navigate('/hirewithus/postjob')}
              style={{
                backgroundColor: getColor('primary'),
                color: getColor('primaryForeground'),
                borderColor: getColor('primary')
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Post a Job
            </Button>
            </Can>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="w-full py-4">
      {title && (
        <Heading level="h4" className="mb-4">
          {title}
        </Heading>
      )}
      
      <div className="rounded-lg border border-border bg-card">
        <div className="overflow-hidden">
          <Table className="border-collapse">
            <TableHeader>
              <TableRow className="bg-muted/50 border-0">
                <TableHead className="w-[24%] h-12 px-4 font-semibold text-foreground border-0 !border-0">Job</TableHead>
                <TableHead className="w-[10%] h-12 px-4 font-semibold text-foreground border-0 !border-0">Status</TableHead>
                <TableHead className="w-[14%] h-12 px-4 font-semibold text-foreground border-0 !border-0">Open Date</TableHead>
                <TableHead className="w-[14%] h-12 px-4 font-semibold text-foreground border-0 !border-0">Close Date</TableHead>
                <TableHead className="w-[16%] h-12 px-4 font-semibold text-foreground border-0 !border-0">Applications</TableHead>
                <TableHead className="w-[22%] h-12 px-4 text-right font-semibold text-foreground border-0 !border-0">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job, index) => {
                const requirementStatus = roleStatus(job);
                const isRoleActive = ['', 'active', 'open'].includes(requirementStatus);
                const isRoleInactive = !isRoleActive;
                let isExpired = !isRoleInactive && job.status === 'expired';
                const isPinned = Boolean(job.is_pinned);
                const daysRemain = Math.ceil((new Date(job.expirationDate) - new Date()) / (1000 * 60 * 60 * 24));
                let daysRemaining;
                if (!isRoleInactive && daysRemain < 0) {
                  isExpired = true;
                } else {
                  daysRemaining = isExpired
                    ? 'Expired'
                    : `${daysRemain} days remaining`;
                }
                if (isExpired) {
                  daysRemaining = 'Expired';
                } else if (job.expirationDate) {
                  const expirationDate = new Date(job.expirationDate);
                  const today = new Date();
                  if (isNaN(expirationDate.getTime())) {
                    daysRemaining = 'Invalid date';
                  } else {
                    const days = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
                    daysRemaining = `${days} days remaining`;
                  }
                } else {
                  daysRemaining = 'No expiration date';
                }
                if (isRoleInactive) {
                  daysRemaining = 'Role inactive';
                }
                const statusLabel = isExpired
                  ? 'Expired'
                  : isRoleActive
                    ? 'Active'
                    : 'Inactive';
                const openDate = job.opened_at || job.openedAt || job.requirement_date || job.requirementDate || job.created_at;
                const closeDate = job.closed_at || job.closedAt;

                return (
                  <TableRow key={job.id || job.job_id || index} className="hover:bg-muted/30 border-0 !border-0">
                    <TableCell className="py-4 px-4 border-0 !border-0">
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold text-foreground">
                          <span className="inline-flex items-center gap-2">
                            {job.jobTitle || job.job_title}
                            {isPinned && (
                              <span className="inline-flex items-center text-xs font-semibold text-primary">
                                <Pin className="w-3.5 h-3.5 mr-1" />
                                Pinned
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span>{job.jobType || job.job_type}</span>
                          <span className="mx-1">•</span>
                          <span>{daysRemaining}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-4 border-0 !border-0">
                      <Badge 
                        variant={isExpired ? "destructive" : "default"}
                        className={cn(
                          "capitalize",
                          isRoleInactive && "border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-100",
                        )}
                        style={!isExpired && isRoleActive ? {
                          backgroundColor: getColor('primary'),
                          color: getColor('primaryForeground'),
                          borderColor: getColor('primary')
                        } : undefined}
                      >
                        {statusLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-4 px-4 border-0 !border-0 text-sm text-foreground">
                      {formatRoleDate(openDate)}
                    </TableCell>
                    <TableCell className="py-4 px-4 border-0 !border-0 text-sm text-foreground">
                      {formatRoleDate(closeDate)}
                    </TableCell>
                    <TableCell className="py-4 px-4 border-0 !border-0">
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="w-4 h-4 text-primary" />
                        </div>
                        <span>{job.applications || job.applicants || 0} Applications</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right py-4 px-4 border-0 !border-0">
                      <div className="flex items-center justify-end gap-2">
                        {/* Eye / EyeOff — public-board visibility toggle.
                            Only rendered when the parent supplied the
                            visibility props, so this component stays
                            backward-compatible with non-employer callers. */}
                        {onToggleVisibility && (
                          <VisibilityToggleButton
                            jobId={job.id || job.job_id}
                            isPublic={Boolean(
                              visibilityMap?.[job.id || job.job_id]
                            )}
                            companyIsPublic={companyIsPublic}
                            onToggle={onToggleVisibility}
                          />
                        )}

                        {handleViewApplications && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleViewApplications(job.id || job.job_id)}
                            style={{
                              backgroundColor: getColor('primary'),
                              color: getColor('primaryForeground'),
                              borderColor: getColor('primary')
                            }}
                          >
                            View Applications
                          </Button>
                        )}

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-9 w-9"
                            >
                              <MoreVertical className="w-4 h-4" />
                              <span className="sr-only">More actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {onViewJobDescription && (
                              <DropdownMenuItem onClick={() => handleDropdownAction('view', job.id || job.job_id)}>
                                <FileText className="mr-2 h-4 w-4" />
                                View Job Description
                              </DropdownMenuItem>
                            )}
                            {onPinJob && (
                              <DropdownMenuItem onClick={() => handleDropdownAction('pin', job.id || job.job_id)}>
                                <Pin className="mr-2 h-4 w-4" />
                                {isPinned ? "Unpin Job Description" : "Pin Job Description"}
                              </DropdownMenuItem>
                            )}
                            {onBroadcastJob && (
                              <DropdownMenuItem onClick={() => handleDropdownAction('broadcast', job.id || job.job_id)}>
                                <Megaphone className="mr-2 h-4 w-4" />
                                Broadcast Job
                              </DropdownMenuItem>
                            )}
                            {(onViewJobDescription || onPinJob || onBroadcastJob) && onDeleteJob && (
                              <DropdownMenuSeparator />
                            )}
                            {onDeleteJob && (
                              <DropdownMenuItem
                                onClick={() => handleDropdownAction('delete', job.id || job.job_id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Job Description
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </section>
  );
}

/**
 * VisibilityToggleButton
 *
 * Small icon-only button that flips a single job's `is_public`. The
 * tooltip carries the full meaning so the UI stays clean even when there
 * are many rows.
 *
 * Tri-state visual logic:
 *   • is_public=false                    → grey EyeOff   "Hidden from public board"
 *   • is_public=true & companyIsPublic   → green Eye     "Showing on public board"
 *   • is_public=true & !companyIsPublic  → amber Eye     "Enabled, but company switch is off"
 *
 * The third state is the warning case — the per-job switch is on, but the
 * company-level master is off, so the job won't appear publicly until the
 * employer flips the master switch.
 */
function VisibilityToggleButton({ jobId, isPublic, companyIsPublic, onToggle }) {
  const Icon = isPublic ? Eye : EyeOff;
  const blocked = isPublic && !companyIsPublic; // enabled-but-hidden warning state

  const tooltip = !isPublic
    ? 'Hidden from public board · click to publish'
    : blocked
    ? "Enabled, but your company's public switch is off — turn it on to publish"
    : 'Showing on public board · click to hide';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-9 w-9',
              isPublic && !blocked && 'text-emerald-600 hover:text-emerald-700',
              blocked && 'text-amber-600 hover:text-amber-700',
              !isPublic && 'text-muted-foreground hover:text-foreground',
            )}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(jobId, !isPublic);
            }}
            aria-label={tooltip}
          >
            <Icon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default JobsTable;
